import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import Icon from "../../../components/AppIcon";
import { requestBillingChange } from "../../../api/Billing";

/**
 * CO-226 — the employee-creation refusal that is NOT a failure.
 *
 * The server refuses employee #51 on a plan whose ceiling is 50 (`PLAN_EMPLOYEE_CAP_REACHED`).
 * `add-user.tsx` used to render every refusal identically: a red destructive banner reading
 * "Failed to add users." plus the raw server sentence, with no code switch and no way forward. For
 * this particular refusal that is three lies in one line — nothing failed, nothing is broken, and
 * the operator can in fact fix it in one click. So this state gets its own affordance.
 *
 * ⚠️ THE NUMBERS COME FROM THE SERVER; THE SENTENCE IS BUILT HERE. The server is the only place the
 * actual values exist (plan name, ceiling, current headcount) — the add-employee screen never loads
 * the billing catalogue, and inventing a second copy of the ceiling here is how the two drift apart.
 * But the server has no idea what language the reader is in, so composing the prose there shipped a
 * verbatim ENGLISH paragraph inside translated Arabic and French chrome (measured on dev, 2026-08).
 *
 * So the split is: server sends `details` (a stable `messageKey` plus the values), this component
 * renders `t(messageKey, values)`. `reason` — the server's English sentence — stays as the
 * `defaultValue`, which covers two real cases: a backend older than the structured payload, and a
 * locale that somehow lacks the key. Neither can ever put a raw `planCap.body` on screen.
 *
 * NOT A MODAL, and not the billing surface's "compose a message" dialog either. The operator is
 * mid-task on a form; a dialog asking them to write a paragraph is a second task. One click sends
 * the enquiry with the context already attached, and the message the operator would have typed is
 * still available on the billing screen for anyone who wants to say more.
 *
 * The visual vocabulary is copied from `RestrictedWriteNotice` on purpose — same class of state
 * ("writing is paused, here is the route"), so it must not look like a different kind of thing.
 */

/** The topic prefix lands in the Operator's inbox, so it is English by design (see api/Billing). */
const UPGRADE_TOPIC = "Plan change" as const;

/**
 * `errors[0].details` for `PLAN_EMPLOYEE_CAP_REACHED` (server: `AccountService.PlanEmployeeCapDetails`).
 * Every field optional at the type level: this arrives over the wire from a backend that may predate
 * it, so nothing here may be assumed present.
 */
export interface PlanCapDetails {
  messageKey?: string;
  planName?: string;
  planNameAr?: string;
  planNameFr?: string;
  maxSeats?: number;
  headcount?: number;
}

interface Props {
  /** The server's English refusal sentence. The fallback body when `details` is absent or partial. */
  reason: string;
  /** Structured values for the localized body. Absent on an older backend. */
  details?: PlanCapDetails | null;
  className?: string;
}

/**
 * The plan name is CATALOGUE DATA, not a translation key, so it ships in all three languages and is
 * picked by the active locale. Falls back through en → the raw name, never to an empty string, so an
 * Arabic reader on a plan with no Arabic name sees "Pro" rather than a hole in the sentence.
 */
function localizedPlanName(details: PlanCapDetails, language: string): string {
  const lang = (language || "en").toLowerCase();
  if (lang.startsWith("ar")) return details.planNameAr || details.planName || "";
  if (lang.startsWith("fr")) return details.planNameFr || details.planName || "";
  return details.planName || "";
}

type SendState = "idle" | "sending" | "sent" | "failed";

const PlanCapUpgradeNotice: React.FC<Props> = ({ reason, details, className }) => {
  const { t, i18n } = useTranslation();
  const [sendState, setSendState] = useState<SendState>("idle");

  // Localized only when the server sent EVERY value the sentence interpolates. A half-filled
  // payload would render "the plan includes up to employees", which is worse than English prose.
  const planName = details ? localizedPlanName(details, i18n.language) : "";
  const canLocalize =
    !!details &&
    !!planName &&
    typeof details.maxSeats === "number" &&
    typeof details.headcount === "number";

  const body = canLocalize
    ? // Namespace-pinned: the key travels over the wire, so it may only ever address this
      // component's own strings. Anything else falls back to the canonical body key.
      t(details!.messageKey?.startsWith("planCap.") ? details!.messageKey : "planCap.body", {
        defaultValue: reason,
        planName,
        maxSeats: details!.maxSeats,
        headcount: details!.headcount,
      })
    : reason;

  const requestUpgrade = async () => {
    if (sendState === "sending" || sendState === "sent") return;
    setSendState("sending");
    try {
      // Written in English and composed here rather than typed by the customer: it is read by a
      // Mowazaf operator, and it must say WHICH screen the request came from so the follow-up
      // starts from the right place.
      const result = await requestBillingChange(
        UPGRADE_TOPIC,
        "Reached the plan employee limit while adding an employee. Requesting an upgrade to Enterprise.",
      );
      setSendState(result.status >= 400 || (result.errors?.length ?? 0) > 0 ? "failed" : "sent");
    } catch {
      setSendState("failed");
    }
  };

  return (
    <div
      role="status"
      data-testid="plan-employee-cap-notice"
      className={`flex items-start gap-3 rounded-lg border border-warning/40 bg-warning/10 p-4 ${className ?? ""}`}
    >
      <Icon name="AlertTriangle" size={18} className="mt-0.5 shrink-0 text-warning" />
      <div className="min-w-0 space-y-1">
        <p className="text-sm font-medium text-foreground">{t("planCap.heading")}</p>
        <p className="text-sm text-muted-foreground">{body}</p>

        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 pt-1">
          {sendState === "sent" ? (
            <span className="inline-flex items-center gap-1.5 text-sm font-medium text-success">
              <Icon name="Check" size={14} className="shrink-0" />
              {t("planCap.requestSent")}
            </span>
          ) : (
            <button
              type="button"
              onClick={requestUpgrade}
              disabled={sendState === "sending"}
              className="text-sm font-medium text-primary underline-offset-4 transition-opacity duration-150 hover:underline disabled:pointer-events-none disabled:opacity-60"
            >
              {sendState === "sending" ? t("planCap.requesting") : t("planCap.requestUpgrade")}
            </button>
          )}

          <Link
            to="/settings/billing"
            className="text-sm font-medium text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
          >
            {t("planCap.goToBilling")}
          </Link>
        </div>

        {sendState === "failed" && (
          <p className="pt-1 text-sm text-muted-foreground">{t("planCap.requestFailed")}</p>
        )}
      </div>
    </div>
  );
};

export default PlanCapUpgradeNotice;
